import { ReferringProviderType } from '~/services/ReferralService/useGetReferringProviders';

// Credential/degree tokens stripped before comparing name components. Stored
// without punctuation so normalized tokens like "PA-C" -> "PAC" match here.
const CREDENTIALS = new Set([
    'MD',
    'DO',
    'NP',
    'FNP',
    'FNPC',
    'PA',
    'PAC',
    'APRN',
    'DNP',
    'OD',
    'DDS',
    'DMD',
    'PHD',
    'RN',
    'MSN',
    'MPH',
    'DPM',
    'AGNP',
    'WHNP',
    'PMHNP',
    'CNP',
    'ARNP',
    'MBBS',
    'CRNP',
    'PHARMD',
    'AUD',
    'LCSW',
    'DABFM',
]);

// Placeholder PCP value used in source data for patients without a real PCP.
const NO_ACTIVE_PCP = 'NO ACTIVE PCP';

export interface ParsedPcpName {
    last: string;
    first: string;
}

// Uppercase, strip accents/punctuation, and collapse whitespace so both the
// Zephyr PCP string and LeadingReach provider components compare consistently.
export function normalize(value: string | null | undefined): string {
    return (value ?? '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z\s-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function stripCredentials(tokens: string[]): string[] {
    // Compare without hyphens so hyphenated credentials ("PA-C", "FNP-C") match
    // the punctuation-free forms in CREDENTIALS while genuine hyphenated
    // surnames ("SMITH-JONES") are preserved.
    return tokens.filter((token) => !CREDENTIALS.has(token.replace(/-/g, '')));
}

// Parses a Zephyr PCP name ("LAST <CRED>, FIRST MIDDLE") into normalized,
// credential-stripped surname and given components. When there is no comma the
// entire string is treated as the surname (given name left empty).
export function parsePcpName(pcpName: string): ParsedPcpName {
    const commaIndex = pcpName.lastIndexOf(',');
    const [leftRaw, rightRaw] =
        commaIndex === -1
            ? [pcpName, '']
            : [pcpName.slice(0, commaIndex), pcpName.slice(commaIndex + 1)];

    const last = stripCredentials(normalize(leftRaw).split(' ').filter(Boolean)).join(' ');
    const first = stripCredentials(normalize(rightRaw).split(' ').filter(Boolean))[0] ?? '';

    return { last, first };
}

// Names that can never resolve to a person and should not be matched or counted
// as failures: empty values, the "NO ACTIVE PCP" placeholder, and doubled
// facility names (identical left/right segments, e.g. "OPTUM X, OPTUM X").
export function isUnmatchablePcpName(pcpName: string | null | undefined): boolean {
    const normalized = normalize(pcpName);
    if (!normalized || normalized === NO_ACTIVE_PCP) {
        return true;
    }

    const commaIndex = pcpName!.lastIndexOf(',');
    if (commaIndex !== -1) {
        const left = normalize(pcpName!.slice(0, commaIndex));
        const right = normalize(pcpName!.slice(commaIndex + 1));
        if (left && left === right) {
            return true;
        }
    }

    return false;
}

// Tier-1 component match: match the Zephyr PCP name to a
// single provider by normalized, credential-stripped last + first name.
// Returns the provider only when exactly one candidate matches; returns null for
// no match or an ambiguous (multiple) match.
export function matchReferringProvider(
    pcpName: string | null | undefined,
    providers: ReferringProviderType[]
): ReferringProviderType | null {
    if (isUnmatchablePcpName(pcpName)) {
        return null;
    }

    const { last, first } = parsePcpName(pcpName as string);
    if (!last || !first) {
        return null;
    }

    const matches = providers.filter(
        (provider) =>
            normalize(provider.last_name) === last && normalize(provider.first_name) === first
    );

    return matches.length === 1 ? matches[0] : null;
}
